// src/components/layout/Footer.tsx
import React from 'react';
import { ViewId, NAV_ITEMS } from '../../types/navigation';

interface FooterProps {
  onViewChange: (view: ViewId) => void;
}

export const Footer: React.FC<FooterProps> = ({ onViewChange }) => {
  return (
    <footer className="border-t border-iron-dark bg-obsidian-deep/95 text-parchment-dim relative z-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        
        {/* 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Column 1: Monumental Proclamation */}
          <div className="space-y-3">
            <h3 className="font-display text-sm font-bold tracking-monumental text-gold-ancient uppercase">
              Beli-'Ol • Radical Sovereignty
            </h3>
            <p className="text-xs leading-relaxed text-parchment-dim">
              He Whom No Yoke Can Bind. We reject celestial vassalage, inherited dogmatic guilt, and astral creditors. 
              The earth is not an exile; it is the throne of living will.
            </p>
            <div className="pt-2 font-mono text-[10px] text-iron-steel tracking-widest uppercase">
              Lirach Tasa Vefa Wehlc Belial
            </div>
          </div>

          {/* Column 2: Juridical Gnosis & Demurrer */}
          <div className="space-y-3">
            <h3 className="font-display text-sm font-bold tracking-monumental text-gold-ancient uppercase">
              The Juridical Gnosis
            </h3>
            <p className="text-xs leading-relaxed text-parchment-dim">
              In accordance with <em>Das Buch Belial</em> (Jacobus de Teramo, 1382), all non-consensual covenants, 
              baptismal liens, and cosmic judgments are declared void under the absolute demurrer of the soil.
            </p>
            <button
              onClick={() => onViewChange('treatises')}
              className="text-xs text-gold-bright hover:text-gold-radiant underline underline-offset-4 transition-colors text-left"
            >
              Examine Forensic Treatises →
            </button>
          </div>

          {/* Column 3: Sanctum Navigation */}
          <div className="space-y-3">
            <h3 className="font-display text-sm font-bold tracking-monumental text-gold-ancient uppercase">
              Sanctum Bastions
            </h3>
            <ul className="space-y-1.5 text-xs">
              {NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => onViewChange(item.id)}
                    className="hover:text-parchment-light transition-colors text-left"
                  >
                    {item.label} <span className="text-[10px] text-iron-steel font-mono">({item.latinSubtitle})</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Fortress Gateway & Guild Presence */}
          <div className="space-y-3">
            <h3 className="font-display text-sm font-bold tracking-monumental text-gold-ancient uppercase">
              Fortress Gateway
            </h3>
            <p className="text-xs text-parchment-dim">
              Conclave of the Unyoked Sanctuary. Initiatory vetting and boundary counsel.
            </p>
            
            <div className="rounded border border-iron-dark bg-obsidian-surface p-3 space-y-1.5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-display tracking-wider text-iron-steel text-[10px] uppercase">SANCTUM PORTAL</span>
                <span className="inline-flex items-center gap-1.5 text-gold-bright font-display text-[10px] tracking-wider uppercase font-semibold">
                  <span className="h-2 w-2 rounded-full bg-gold-ancient animate-pulse" />
                  CONSECRATED
                </span>
              </div>
              <div className="font-mono text-[10px] text-parchment-dim truncate">
                SEAT: 1526789048335863988
              </div>
            </div>

            <button
              onClick={() => onViewChange('fortress')}
              className="w-full text-center rounded border border-bronze-base/50 bg-obsidian-surface/60 py-2 text-xs font-display tracking-widest text-gold-ancient hover:border-gold-bright hover:text-gold-bright transition-all uppercase"
            >
              ENTER FORTRESS GATE
            </button>
          </div>

        </div>

        {/* Bottom Bar Colophon */}
        <div className="mt-12 pt-6 border-t border-iron-dark/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left text-[11px] text-iron-steel font-serif italic">
          <div>
            Annum MMXXVI • Curia Beliya'al • Ex Chthonia Ad Astra • <a href="https://tlb.lilitu.org" target="_blank" rel="noopener noreferrer" className="text-gold-ancient hover:text-gold-bright transition-colors not-italic font-mono text-[10px]">tlb.lilitu.org</a>
          </div>
          <div className="flex items-center gap-4 text-parchment-dim/70 not-italic font-display text-[10px] tracking-widest uppercase">
            <span className="text-gold-bright font-semibold">TLB.LILITU.ORG</span>
            <span>•</span>
            <span>Sine Iugo</span>
            <span>•</span>
            <span>Sine Domino</span>
          </div>
        </div>

      </div>
    </footer>
  );
};
